import React, { useState, useEffect, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  RefreshCw,
  GitBranch,
  Bot,
  Shield,
  User,
  Settings,
  RotateCcw,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
} from 'lucide-react';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { workflowApi } from '@/services/api/workflowApi';

interface WorkflowVersionEntry {
  _id: string;
  workflowId: string;
  version: number;
  changeType: 'initial' | 'agent_request' | 'supervisor_fix' | 'user_edit' | 'rollback';
  requestedBy: 'agent' | 'supervisor' | 'user' | 'system';
  reason: string;
  changeDescription?: string;
  previousVersion?: number;
  executionMetrics?: {
    successRate?: number;
    avgExecutionTime?: number;
    llmCallsPerCycle?: number;
    executionsSinceVersion?: number;
  };
  createdAt: string;
}

const changeTypeConfig: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  initial: { label: 'Created', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: <GitBranch className="w-3 h-3" /> },
  agent_request: { label: 'Agent Change', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: <Bot className="w-3 h-3" /> },
  supervisor_fix: { label: 'Supervisor Fix', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', icon: <Shield className="w-3 h-3" /> },
  user_edit: { label: 'User Edit', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: <User className="w-3 h-3" /> },
  rollback: { label: 'Rollback', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: <RotateCcw className="w-3 h-3" /> },
};

const requestedByIcons: Record<string, React.ReactNode> = {
  agent: <Bot className="w-3.5 h-3.5" />,
  supervisor: <Shield className="w-3.5 h-3.5" />,
  user: <User className="w-3.5 h-3.5" />,
  system: <Settings className="w-3.5 h-3.5" />,
};

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const VersionEntry: React.FC<{ entry: WorkflowVersionEntry }> = ({ entry }) => {
  const [expanded, setExpanded] = useState(false);
  const typeConfig = changeTypeConfig[entry.changeType] || changeTypeConfig.initial;

  return (
    <div className="border rounded-md bg-card">
      <button
        className="w-full text-left p-3 hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-2">
          <div className="mt-0.5 text-muted-foreground">
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${typeConfig.color}`}>
                <span className="mr-1">{typeConfig.icon}</span>
                {typeConfig.label}
              </Badge>
              <span className="text-xs text-muted-foreground font-mono">v{entry.version}</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(entry.createdAt)}
              </span>
            </div>
            <p className="text-sm mt-1.5 line-clamp-2">{entry.reason}</p>
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
              {requestedByIcons[entry.requestedBy]}
              <span className="capitalize">{entry.requestedBy}</span>
              {entry.previousVersion != null && (
                <span className="ml-2">from v{entry.previousVersion}</span>
              )}
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-0 border-t space-y-2">
          {entry.changeDescription && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Description</span>
              <p className="text-xs mt-0.5">{entry.changeDescription}</p>
            </div>
          )}
          {entry.executionMetrics && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Metrics at time of change</span>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {entry.executionMetrics.successRate != null && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Success: </span>
                    <span className="font-mono">{(entry.executionMetrics.successRate * 100).toFixed(0)}%</span>
                  </div>
                )}
                {entry.executionMetrics.avgExecutionTime != null && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Avg time: </span>
                    <span className="font-mono">{entry.executionMetrics.avgExecutionTime}ms</span>
                  </div>
                )}
                {entry.executionMetrics.llmCallsPerCycle != null && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">LLM/cycle: </span>
                    <span className="font-mono">{entry.executionMetrics.llmCallsPerCycle.toFixed(2)}</span>
                  </div>
                )}
                {entry.executionMetrics.executionsSinceVersion != null && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Executions: </span>
                    <span className="font-mono">{entry.executionMetrics.executionsSinceVersion}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="text-[10px] text-muted-foreground font-mono">
            {new Date(entry.createdAt).toLocaleString()}
          </div>
        </div>
      )}
    </div>
  );
};

export const WorkflowModificationLogTab: React.FC = () => {
  const { workflowId, workflow, convertToAgent } = useWorkflowEditor();
  const [converting, setConverting] = useState(false);
  const [versions, setVersions] = useState<WorkflowVersionEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAgentWorkflow = workflow?.type === 'agent';

  const fetchVersions = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await workflowApi.getWorkflowVersions(workflowId);
      setVersions(result.data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  if (!isAgentWorkflow) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-6">
        <Bot className="w-12 h-12 mb-3 opacity-50" />
        <p className="text-sm font-medium">Not an agent workflow</p>
        <p className="text-xs mt-1 text-center mb-4">
          Convert this workflow to an agent to enable LLM reasoning, supervised modifications, and autonomous operation.
        </p>
        <Button
          size="sm"
          onClick={async () => {
            setConverting(true);
            await convertToAgent();
            setConverting(false);
          }}
          disabled={converting}
        >
          <Bot className="w-3.5 h-3.5 mr-1.5" />
          {converting ? 'Converting...' : 'Convert to Agent'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">
              {versions.length} version{versions.length !== 1 ? 's' : ''}
            </span>
            {workflow?.agentConfig?.generation != null && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                Gen {workflow.agentConfig.generation}
              </Badge>
            )}
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={fetchVersions} disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Agent Config Summary */}
      {workflow?.agentConfig && (
        <div className="p-3 border-b text-xs space-y-1 flex-shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Autonomy</span>
            <Badge variant="outline" className="text-[10px] capitalize">
              {workflow.agentConfig.autonomyLevel || 'guided'}
            </Badge>
          </div>
          {workflow.agentConfig.maxGenerations && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Max generations</span>
              <span className="font-mono">{workflow.agentConfig.generation || 0}/{workflow.agentConfig.maxGenerations}</span>
            </div>
          )}
        </div>
      )}

      {/* Version List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {error && (
            <div className="flex items-center gap-2 p-2 rounded bg-destructive/10 text-destructive text-xs">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {error}
            </div>
          )}
          {loading && versions.length === 0 && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <RefreshCw className="w-5 h-5 animate-spin" />
            </div>
          )}
          {!loading && versions.length === 0 && !error && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <GitBranch className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-xs">No version history yet</p>
              <p className="text-[10px] mt-1">Changes will appear here when the agent is active</p>
            </div>
          )}
          {versions.map((entry) => (
            <VersionEntry key={entry._id} entry={entry} />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
